import React from "react";
import { Text, View } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";

import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";

import styles from "../../styles";
import { chunkTableRows, formatDisplayValue } from "../../helpers";

const COLUMN_WIDTHS = ["12%", "18%", "8%", "8%", "22%", "14%", "18%"];
const ROWS_PER_PAGE = 18;
const TABLE_MIN_PRESENCE = 120;

const formatDate = (dateUtils, value) => {
    if (!value) {
        return "-";
    }

    return dateUtils.convertDateToDate(value, INTERNATIONAL_DATE_FORMAT);
};

const resolveManagerName = (manager = {}) => {
    return [
        manager?.title,
        manager?.fullName || manager?.name,
        manager?.lastName,
    ].filter(Boolean).join(" ").trim() || "-";
};

const ExternalManagementTable = ({ t, teamManagement = [] }) => {
    const dateUtils = new DateUtils();
    const managers = Array.isArray(teamManagement) ? teamManagement : [];

    const headerCellStyle = {
        ...styles.tableColHeader,
        ...styles.wordTableHeaderCell,
    };

    const headerTextStyle = {
        ...styles.tableCell,
        ...styles.fontBold,
        ...styles.wordTableHeaderText,
    };

    const renderHeaderCell = (label, width) => (
        <View
            style={{
                ...headerCellStyle,
                width,
            }}
        >
            <Text style={headerTextStyle}>{t(label)}</Text>
        </View>
    );

    const renderBodyCell = (value, width) => (
        <View
            style={{
                ...styles.tableCol,
                width,
            }}
        >
            <Text style={styles.tableCell}>{formatDisplayValue(value)}</Text>
        </View>
    );

    const headerRow = (
        <View style={styles.tableRow}>
            {renderHeaderCell("Title", COLUMN_WIDTHS[0])}
            {renderHeaderCell("Name", COLUMN_WIDTHS[1])}
            {renderHeaderCell("Gender", COLUMN_WIDTHS[2])}
            {renderHeaderCell("ID", COLUMN_WIDTHS[3])}
            {renderHeaderCell("Position", COLUMN_WIDTHS[4])}
            {renderHeaderCell("Date of Birth", COLUMN_WIDTHS[5])}
            {renderHeaderCell("Appointed On", COLUMN_WIDTHS[6])}
        </View>
    );

    const chunks = managers.length
        ? chunkTableRows(managers, ROWS_PER_PAGE)
        : [[]];

    return (
        <View>
            {chunks.map((chunk, chunkIndex) => {
                const isFirstChunk = chunkIndex === 0;
                const globalOffset = chunkIndex * ROWS_PER_PAGE;

                return (
                    <View
                        key={`management-table-chunk-${chunkIndex}`}
                        style={{ ...styles.table, width: "100%" }}
                        wrap={false}
                        minPresenceAhead={isFirstChunk ? TABLE_MIN_PRESENCE : 80}
                    >
                        {headerRow}

                        {chunk.length ? chunk.map((manager, index) => {
                            const absoluteIndex = globalOffset + index;
                            const rowStyle = {
                                ...styles.tableRow,
                                ...(absoluteIndex % 2 === 1 ? styles.tableRowAlt : {}),
                                ...(index === chunk.length - 1 ? styles.tableLastRow : {}),
                            };

                            return (
                                <View
                                    style={rowStyle}
                                    key={manager?.id || `manager-${absoluteIndex}`}
                                >
                                    {renderBodyCell(manager?.title || "Executive", COLUMN_WIDTHS[0])}
                                    {renderBodyCell(resolveManagerName(manager), COLUMN_WIDTHS[1])}
                                    {renderBodyCell(manager?.gender, COLUMN_WIDTHS[2])}
                                    {renderBodyCell(manager?.id || manager?.secondaryId, COLUMN_WIDTHS[3])}
                                    {renderBodyCell(manager?.position, COLUMN_WIDTHS[4])}
                                    {renderBodyCell(
                                        formatDate(dateUtils, manager?.dateOfBirth || manager?.birthDate),
                                        COLUMN_WIDTHS[5]
                                    )}
                                    {renderBodyCell(
                                        formatDate(
                                            dateUtils,
                                            manager?.appointmentDate || manager?.appointedOn || manager?.createdAt
                                        ),
                                        COLUMN_WIDTHS[6]
                                    )}
                                </View>
                            );
                        }) : (
                            <View style={{ ...styles.tableRow, ...styles.tableLastRow }}>
                                {renderBodyCell(t("No Data"), COLUMN_WIDTHS[0])}
                                {renderBodyCell("-", COLUMN_WIDTHS[1])}
                                {renderBodyCell("-", COLUMN_WIDTHS[2])}
                                {renderBodyCell("-", COLUMN_WIDTHS[3])}
                                {renderBodyCell("-", COLUMN_WIDTHS[4])}
                                {renderBodyCell("-", COLUMN_WIDTHS[5])}
                                {renderBodyCell("-", COLUMN_WIDTHS[6])}
                            </View>
                        )}
                    </View>
                );
            })}
        </View>
    );
};

export default withNamespaces()(ExternalManagementTable);
